import dayjs from 'dayjs';
import { createContext, FC, ReactNode, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

export const SEARCH_PARAM_KEYWORD = 'keyword';
export const SEARCH_PARAM_LOCATION = 'location';
export const SEARCH_PARAM_RADIUS = 'radius';
export const SEARCH_PARAM_START_DATE = 'startDate';
export const SEARCH_PARAM_COMPENSATION_MIN = 'compensationMin';
export const SEARCH_PARAM_COMPENSATION_MAX = 'compensationMax';
export const SEARCH_PARAM_COMPENSATION_TYPE = 'compensationType';
export const SEARCH_PARAM_BENEFITS = 'benefits';
export const SEARCH_PARAM_COMMISSION = 'hasCommission';
export const SEARCH_PARAM_COMMITMENT = 'commitmentType';

interface SearchContextProps {
  keyword: string;
  location: string;
  radius: string;
  startDate: string;
  compensationMin: string;
  compensationMax: string;
  compensationType: string;
  benefits: string;
  commission: string;
  commitment: string;
  updateSearchParams: (key: string, value?: string) => void;
}

const SearchContext = createContext<SearchContextProps | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: FC<SearchProviderProps> = ({ children }) => {
  const lastMonth = dayjs().subtract(1, 'month').format('YYYY-MM-DD');

  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get(SEARCH_PARAM_KEYWORD) || '';
  const location = searchParams.get(SEARCH_PARAM_LOCATION) || '';
  const radius = searchParams.get(SEARCH_PARAM_RADIUS) || '160';
  const startDate = searchParams.get(SEARCH_PARAM_START_DATE) || lastMonth;
  const compensationMin = searchParams.get(SEARCH_PARAM_COMPENSATION_MIN) || '';
  const compensationMax = searchParams.get(SEARCH_PARAM_COMPENSATION_MAX) || '';
  const compensationType = searchParams.get(SEARCH_PARAM_COMPENSATION_TYPE) || '';
  const benefits = searchParams.get(SEARCH_PARAM_BENEFITS) || '';
  const commission = searchParams.get(SEARCH_PARAM_COMMISSION) || '';
  const commitment = searchParams.get(SEARCH_PARAM_COMMITMENT) || '';

  const updateSearchParams = (key: string, value?: string) => {
    if (!value || value.trim() === '') {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(key);
      setSearchParams(newParams);
    } else {
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        [key]: value,
      });
    }
  };

  return (
    <SearchContext.Provider 
      value={{ 
        keyword, 
        location, 
        radius, 
        startDate, 
        compensationMin,
        compensationMax,
        compensationType,
        benefits,
        commission,
        commitment,
        updateSearchParams 
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextProps => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
